import { useState, useCallback, useRef } from "react";
import * as THREE from "three";

// Helper function to create a custom plane mesh (Simple).
const createCustomPlaneHelperSimple = (
  planeSimple,
  positionSimple,
  sizeSimple
) => {
  const geometrySimple = new THREE.PlaneGeometry(sizeSimple, sizeSimple);
  const materialSimple = new THREE.MeshBasicMaterial({
    color: 0xffff00,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.2,
    depthWrite: false,
  });
  const meshSimple = new THREE.Mesh(geometrySimple, materialSimple);

  // The default normal for PlaneGeometry is (0,0,1).
  // Compute the quaternion to rotate from (0,0,1) to our plane's normal.
  const defaultNormalSimple = new THREE.Vector3(0, 0, 1);
  const quaternionSimple = new THREE.Quaternion().setFromUnitVectors(
    defaultNormalSimple,
    planeSimple.normal.clone().normalize()
  );
  meshSimple.setRotationFromQuaternion(quaternionSimple);
  meshSimple.position.copy(positionSimple);

  return meshSimple;
};

const useSectionViewSimple = (modelRefSimple, sceneRefSimple) => {
  const [isSectionViewModeSimple, setIsSectionViewModeSimple] = useState(false);
  const [sectionViewModeSimple, setSectionViewModeSimple] = useState("none");
  const [planeOffsetSimple, setPlaneOffsetSimple] = useState(0);
  const [showHelperSimple, setShowHelperSimple] = useState(true);
  const [invertClipSimple, setInvertClipSimple] = useState(true);
  const planeHelperRefSimple = useRef(null);

  const applySectionViewSimple = useCallback(() => {
    if (!modelRefSimple.current || !sceneRefSimple?.current) return;

    // Remove any existing custom helper from the scene.
    if (planeHelperRefSimple.current) {
      sceneRefSimple.current.remove(planeHelperRefSimple.current);
      planeHelperRefSimple.current = null;
    }

    // Compute the model's bounding box, center, and max dimension.
    const boundingBoxSimple = new THREE.Box3().setFromObject(
      modelRefSimple.current
    );
    const centerSimple = new THREE.Vector3();
    boundingBoxSimple.getCenter(centerSimple);
    const sizeVectorSimple = boundingBoxSimple.getSize(new THREE.Vector3());
    const maxDimensionSimple = Math.max(
      sizeVectorSimple.x,
      sizeVectorSimple.y,
      sizeVectorSimple.z
    );

    let clippingPlaneSimple = null;
    let planeNormalSimple = null;
    switch (sectionViewModeSimple) {
      case "xy": {
        planeNormalSimple = new THREE.Vector3(0, 0, 1);
        const constantSimple =
          -planeNormalSimple.dot(centerSimple) - planeOffsetSimple;
        clippingPlaneSimple = new THREE.Plane(
          planeNormalSimple,
          constantSimple
        );
        break;
      }
      case "yz": {
        planeNormalSimple = new THREE.Vector3(1, 0, 0);
        const constantSimple =
          -planeNormalSimple.dot(centerSimple) - planeOffsetSimple;
        clippingPlaneSimple = new THREE.Plane(
          planeNormalSimple,
          constantSimple
        );
        break;
      }
      case "xz": {
        planeNormalSimple = new THREE.Vector3(0, -1, 0);
        const constantSimple =
          -planeNormalSimple.dot(centerSimple) - planeOffsetSimple;
        clippingPlaneSimple = new THREE.Plane(
          planeNormalSimple,
          constantSimple
        );
        break;
      }
      case "custom":
      case "none":
      default:
        clippingPlaneSimple = null;
        break;
    }

    // Use inverted plane for materials when requested; helper uses the original.
    const planeForMaterials =
      clippingPlaneSimple && invertClipSimple
        ? clippingPlaneSimple.clone().negate()
        : clippingPlaneSimple;

    // Update each mesh material with the (possibly inverted) clipping plane.
    modelRefSimple.current.children.forEach((childSimple) => {
      const applyToMat = (mat) => {
        if (!mat) return;
        if (planeForMaterials) {
          mat.clippingPlanes = [planeForMaterials];
          mat.clipShadows = true;
        } else {
          mat.clippingPlanes = [];
        }
        mat.needsUpdate = true;
      };

      if (childSimple.material) {
        if (Array.isArray(childSimple.material)) {
          childSimple.material.forEach(applyToMat);
        } else {
          applyToMat(childSimple.material);
        }
      }
    });

    // If a clipping plane is defined and the helper is enabled, create our custom plane helper.
    if (clippingPlaneSimple && showHelperSimple && planeNormalSimple) {
      const helperPositionSimple = centerSimple
        .clone()
        .add(planeNormalSimple.clone().multiplyScalar(planeOffsetSimple));
      const customHelperSimple = createCustomPlaneHelperSimple(
        clippingPlaneSimple,
        helperPositionSimple,
        maxDimensionSimple * 1.1
      );
      sceneRefSimple.current.add(customHelperSimple);
      planeHelperRefSimple.current = customHelperSimple;
    }
  }, [
    modelRefSimple,
    sceneRefSimple,
    sectionViewModeSimple,
    planeOffsetSimple,
    showHelperSimple,
    invertClipSimple,
  ]);

  return {
    isSectionViewModeSimple,
    sectionViewModeSimple,
    setIsSectionViewModeSimple,
    setSectionViewModeSimple,
    applySectionViewSimple,
    planeOffsetSimple,
    setPlaneOffsetSimple,
    showHelperSimple,
    setShowHelperSimple,
    setInvertClipSimple,
  };
};

export default useSectionViewSimple;
